/**
 * What the Stat Dictionary table shows: searched, filtered and sorted.
 *
 * Several hundred entries in one table — 392 in football alone — is a list
 * nobody reads top to bottom. This module narrows it by what a person types and
 * picks, and orders it by the column they clicked. Like `stat-search.ts` it
 * holds no React and no DOM, so `tests/stat-dictionary-view.test.ts` runs it
 * under bare `node`.
 *
 * **What is searched is what is printed**, the rule `stat-search.ts` states: an
 * entry matches on its key, its display name or one of its aliases, all three
 * of which are columns of the table. The term splitting repeats that module's
 * rather than importing it, because a value import between `src/` modules does
 * not resolve under `node --experimental-strip-types` without an extension, and
 * `tsconfig.json` forbids writing one.
 */

import type { Direction, StatEntry } from "./api";

export type StatColumn = "sport" | "key" | "display_name" | "direction" | "weight";

export interface StatSort {
  column: StatColumn;
  descending: boolean;
}

export interface StatView {
  query: string;
  /** "" for every sport. */
  sport: string;
  /** "" for either Direction. */
  direction: Direction | "";
  /** "" for any, otherwise whether a default weight is declared. */
  weight: "" | "declared" | "undeclared";
  sort: StatSort;
}

export const DEFAULT_VIEW: StatView = {
  query: "",
  sport: "",
  direction: "",
  weight: "",
  sort: { column: "key", descending: false },
};

/** Whether anything narrows the table, which is when "Clear" is worth showing. */
export function isNarrowed(view: StatView): boolean {
  return view.query.trim() !== "" || view.sport !== "" || view.direction !== "" || view.weight !== "";
}

export function viewStats(entries: readonly StatEntry[], view: StatView): StatEntry[] {
  const wanted = terms(view.query);
  const shown = entries.filter(
    (entry) =>
      (view.sport === "" || entry.sport === view.sport) &&
      (view.direction === "" || entry.direction === view.direction) &&
      (view.weight === "" || (entry.default_weight != null) === (view.weight === "declared")) &&
      matches(entry, wanted),
  );
  return shown.sort((a, b) => compare(a, b, view.sort));
}

function terms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((term) => term !== "");
}

function normalise(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/** Every term must appear in at least one of the printed texts. */
function matches(entry: StatEntry, wanted: readonly string[]): boolean {
  if (wanted.length === 0) return true;
  const texts = [entry.key, entry.display_name, ...entry.aliases].map(normalise);
  return wanted.every((term) => texts.some((text) => text.includes(term)));
}

/**
 * One column's order, ties broken by sport and then key so the order is total.
 *
 * An entry with no default weight sorts last whichever way the column runs: it
 * is "nothing declared", not a weight of zero, and reversing the sort to find
 * the heaviest statistics should not open on a page of blanks.
 */
function compare(a: StatEntry, b: StatEntry, sort: StatSort): number {
  const flip = sort.descending ? -1 : 1;
  let order = 0;
  switch (sort.column) {
    case "weight":
      if (a.default_weight == null || b.default_weight == null) {
        if (a.default_weight != b.default_weight) return a.default_weight == null ? 1 : -1;
      } else {
        order = (a.default_weight - b.default_weight) * flip;
      }
      break;
    case "display_name":
      order = a.display_name.localeCompare(b.display_name) * flip;
      break;
    default:
      order = a[sort.column].localeCompare(b[sort.column]) * flip;
  }
  return order || a.sport.localeCompare(b.sport) || a.key.localeCompare(b.key);
}
